import React from "react";
import { Table } from "antd";
import { useTranslation } from "react-i18next";
import { useGetProviders, useVerifyProviders } from "../../queries/provider";
import { ServiceAccountStatusTag } from "./ServiceAccountStatusTag";
import {
  Provider,
  ProviderStatusResponse,
  ServiceAccountStatus,
} from "../../types/provider";
import { ServiceAccountStatusTooltip } from "./ServiceAccountStatusTooltip";
import { ServiceAccountsTableActions } from "./ServiceAccountsTableActions/ServiceAccountsTableActions";

const ServiceAccountsTable = () => {
  const { t } = useTranslation();
  const { data: providers } = useGetProviders();
  const {
    data: providerStatus,
    fetchStatus,
    refetch: refetchStatuses,
  } = useVerifyProviders({
    enabled: !!providers[0]?.id,
  });

  const columns = [
    {
      title: t("pages.service-accounts.service-accounts-table.email"),
      dataIndex: "accountAddress",
      key: "accountAddress",
      defaultSortOrder: "ascend" as const,
      sorter: (a: { accountAddress: string }, b: { accountAddress: string }) =>
        a.accountAddress.localeCompare(b.accountAddress),
    },
    {
      title: t("pages.service-accounts.service-accounts-table.connected-by"),
      dataIndex: "createdBy",
      key: "createdBy",
    },
    {
      title: t("pages.service-accounts.service-accounts-table.provider"),
      dataIndex: "providerName",
      key: "providerName",
    },
    {
      title: (
        <>
          {t("pages.service-accounts.service-accounts-table.status")}
          <ServiceAccountStatusTooltip />
        </>
      ),
      dataIndex: "status",
      key: "status",
      width: "9rem",
      render: (status: ServiceAccountStatus) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ServiceAccountStatusTag status={status} />
        </div>
      ),
    },
    {
      title: t("action"),
      key: "action",
      width: "7rem",
      render: (provider: Provider & ProviderStatusResponse) => (
        <ServiceAccountsTableActions
          provider={provider}
          refetchStatuses={refetchStatuses}
        />
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={providers?.map((provider) => ({
        ...provider,
        status:
          providerStatus?.find((status) => status.id === provider.id)?.status ||
          (fetchStatus === "fetching" ? "processing" : "waiting"),
      }))}
      bordered
      title={() => (
        <strong>
          {t("pages.service-accounts.service-accounts-table.title")}
        </strong>
      )}
      style={{ width: "100%" }}
      rowKey={(record) => record.id}
    />
  );
};

export default ServiceAccountsTable;
